import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckIcon, CircleDotIcon, CircleIcon, DownloadIcon, SendIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { usePortalAuth } from '../../context/PortalAuth';
import { PROJECTS, DELIVERABLES, STATUS_LABELS, type PortalMessage } from '../../data/portal';
import { requestDownloadUrl } from '../../lib/leads';
import { track } from '../../lib/analytics';
export function PortalProject() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const {
    user
  } = usePortalAuth();
  const [draft, setDraft] = React.useState('');
  const [thread, setThread] = React.useState<PortalMessage[]>([]);
  const [initialised, setInitialised] = React.useState(false);
  const project = PROJECTS.find(item => item.id === id);
  const permitted = Boolean(project && user && project.organisationId === user.organisationId);
  React.useEffect(() => {
    if (project && !initialised) {
      setThread(project.messages);
      setInitialised(true);
    }
  }, [project, initialised]);
  if (!user) return null;
  if (!permitted || !project) {
    return <div className="rounded-md border border-line bg-white p-8" data-id="element-1077" data-mp="ds1z12csgerq">
        <h1 className="font-display text-[26px] text-ink" data-id="element-1078" data-mp="3v1gv32r3zbj9">This project is not available to you</h1>
        <p className="mt-3 text-[16px] text-body" data-id="element-1079" data-mp="3unp7lldeo8e">
          Access is checked against your organisation on every request. If you believe this is an error, contact
          your account manager.
        </p>
        <Button to="/portal/dashboard" variant="secondary" className="mt-6" data-id="element-1080" data-mp="3r05711nvuyd6">
          Back to the dashboard
        </Button>
      </div>;
  }
  const files = DELIVERABLES.filter(item => item.projectId === project.id);
  const download = async (fileName: string) => {
    const result = await requestDownloadUrl(fileName, {
      kind: 'deliverable',
      id: project.id
    });
    if (!result.url) return;
    track('deliverable_downloaded', {
      project: project.id,
      file: fileName
    });
    window.open(result.url, '_blank', 'noopener');
  };
  const send = (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.trim()) return;
    setThread(current => [...current, {
      at: new Date().toISOString().slice(0, 10),
      from: user.name,
      fromCavalla: false,
      text: draft.trim()
    }]);
    setDraft('');
  };
  return <div className="space-y-8" data-id="element-1081" data-mp="z26e5z0hyeg5">
      <header data-id="element-1082" data-mp="1wsazxoztio9r">
        <nav aria-label="Breadcrumb" data-id="element-1083" data-mp="1wqq9aagmjph6">
          <Link to="/portal/dashboard" className="text-small text-royal underline-offset-4 hover:underline" data-id="element-1084" data-mp="1wpoubuwhmiu6">
            Dashboard
          </Link>
        </nav>
        <p className="mt-4 text-small uppercase tracking-[0.12em] text-muted" data-id="element-1085" data-mp="1msk9byedotey">{project.serviceLine}</p>
        <h1 className="mt-2 font-display text-[28px] leading-tight text-ink sm:text-[34px]" data-id="element-1086" data-mp="1ir8dvc8tpdyu">{project.name}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-small text-muted" data-id="element-1087" data-mp="bfzwel19tkcq">
          <span className="rounded-full bg-champagne px-3 py-1 font-semibold uppercase tracking-[0.12em] text-ink" data-id="element-1088" data-mp="7c1ww981a7j5">
            {STATUS_LABELS[project.status]}
          </span>
          <span data-id="element-1089" data-mp="2n10eiiuyf3lk">
            Started{' '}
            {new Date(project.startDate).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
          </span>
          <span aria-hidden="true" data-id="element-1090" data-mp="2wrbj87mgpgti">·</span>
          <span data-id="element-1091" data-mp="2wsd5z9cubrr2">
            Target{' '}
            {new Date(project.targetDate).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
          </span>
        </div>
      </header>

      <section aria-labelledby="scope" className="rounded-md border border-line bg-white p-6" data-id="element-1092" data-mp="36o7ugl3qwh1r">
        <h2 id="scope" className="font-display text-[20px] text-ink" data-id="element-1093" data-mp="3arw377445iol">
          Scope
        </h2>
        <p className="mt-3 max-w-prose text-[16px] text-body" data-id="element-1094" data-mp="3fpxkpnlw59xy">{project.summary}</p>
      </section>

      <section aria-labelledby="milestones" className="rounded-md border border-line bg-white p-6" data-id="element-1095" data-mp="1gewfsrthrk6r">
        <h2 id="milestones" className="font-display text-[20px] text-ink" data-id="element-1096" data-mp="1lcy905kmszvy">
          Phases and milestones
        </h2>
        <ol className="mt-5 space-y-4" data-id="element-1097" data-mp="q4ck9mthqgpt">
          {project.milestones.map(milestone => {
          const Icon = milestone.status === 'complete' ? CheckIcon : milestone.status === 'in-progress' ? CircleDotIcon : CircleIcon;
          return <li key={milestone.name} className="flex items-start gap-3" data-id="element-1098" data-mp="7b8nvtow1pri">
                <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${milestone.status === 'upcoming' ? 'text-line' : 'text-gold'}`} aria-hidden="true" data-id="element-1099" data-mp="79fisitxbdlm" />
                <div data-id="element-1100" data-mp="cal806odkiqe">
                  <p className={`text-[16px] ${milestone.status === 'upcoming' ? 'text-muted' : 'text-ink'}`} data-id="element-1101" data-mp="ca37gxuojico">
                    {milestone.name}
                  </p>
                  <p className="mt-0.5 text-small text-muted" data-id="element-1102" data-mp="1v9sz0s64kbsa">
                    {milestone.status === 'complete' ? 'Completed by ' : 'Due '}
                    {new Date(milestone.due).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
                  </p>
                </div>
              </li>;
        })}
        </ol>
      </section>

      <section aria-labelledby="project-files" className="rounded-md border border-line bg-white p-6" data-id="element-1103" data-mp="2dxxvdekgcuh2">
        <h2 id="project-files" className="font-display text-[20px] text-ink" data-id="element-1104" data-mp="2ix100xwisyoi">
          Files for this project
        </h2>
        <ul className="mt-5 divide-y divide-line" data-id="element-1105" data-mp="290o0hjeanqlu">
          {files.map(file => <li key={file.id} className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-id="element-1106" data-mp="tk70rosym4k0">
              <div data-id="element-1107" data-mp="xlsns2z7jz7x">
                <p className="text-[15px] text-ink" data-id="element-1108" data-mp="xmaharuhjzc5">{file.name}</p>
                <p className="mt-0.5 text-small text-muted" data-id="element-1109" data-mp="xjfw2y3neaho">
                  {file.type} · {file.version} · {(file.sizeKb / 1024).toFixed(1)} MB · uploaded by{' '}
                  {file.uploadedBy}
                </p>
              </div>
              <button type="button" onClick={() => download(file.fileName)} className="inline-flex items-center gap-2 rounded-sm border border-royal/30 px-4 py-2 text-[14px] text-royal transition-colors hover:bg-mist" data-id="element-1110" data-mp="2qhuuzr9d55za">
                <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1111" data-mp="2cic0i19h5t5g" />
                Download
              </button>
            </li>)}
        </ul>
      </section>

      <section aria-labelledby="activity" className="rounded-md border border-line bg-white p-6" data-id="element-1112" data-mp="27llkfdxo6xk1">
        <h2 id="activity" className="font-display text-[20px] text-ink" data-id="element-1113" data-mp="22n1z9fqwhpsq">
          Activity
        </h2>
        <ol className="mt-5 space-y-4 border-l border-line pl-5" data-id="element-1114" data-mp="tb20pln0jst">
          {project.activity.map(entry => <li key={`${entry.at}-${entry.text}`} className="relative" data-id="element-1115" data-mp="9al1me3q442r">
              <span aria-hidden="true" className="absolute -left-[26px] top-2 h-2 w-2 rounded-full bg-gold" data-id="element-1116" data-mp="98iga7dimz4d" />
              <p className="text-[15px] text-body" data-id="element-1117" data-mp="i3oeolagiewv">{entry.text}</p>
              <p className="mt-0.5 text-small text-muted" data-id="element-1118" data-mp="n1gfk4t2xbd7">
                {entry.actor} ·{' '}
                {new Date(entry.at).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
              </p>
            </li>)}
        </ol>
      </section>

      <section aria-labelledby="thread" className="rounded-md border border-line bg-white p-6" data-id="element-1119" data-mp="3vhjcbfinlfmr">
        <h2 id="thread" className="font-display text-[20px] text-ink" data-id="element-1120" data-mp="4b8hw285fd4e">
          Message the team
        </h2>
        <ul className="mt-5 space-y-4" data-id="element-1121" data-mp="1arkisb2cv93o">
          {thread.map((message, index) => <li key={`${message.at}-${index}`} className={`rounded-md p-4 ${message.fromCavalla ? 'bg-champagne' : 'bg-mist'}`} data-id="element-1122" data-mp="1fqf018kocplo" data-mpi={index}>
              <p className="text-[15px] leading-relaxed text-body" data-id="element-1123" data-mp="20upk9lfunc9h">{message.text}</p>
              <p className="mt-2 text-small text-muted" data-id="element-1124" data-mp="1wu5pgoir0kqi">
                {message.from} ·{' '}
                {new Date(message.at).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long'
            })}
              </p>
            </li>)}
        </ul>
        <form onSubmit={send} className="mt-5" data-id="element-1125" data-mp="2ki6rrfpc34xh">
          <label htmlFor="thread-input" className="block text-[14px] font-medium text-ink" data-id="element-1126" data-mp="2kgvnlerz7v5u">
            Your message
          </label>
          <div className="mt-2 flex gap-2" data-id="element-1127" data-mp="2tj4vrxzrvcg2">
            <textarea id="thread-input" rows={3} value={draft} onChange={event => setDraft(event.target.value)} className="w-full rounded-sm border border-line px-3.5 py-2.5 text-[15px] text-ink" data-id="element-1128" data-mp="2ti26tvm0fk2s" />
          </div>
          <button type="submit" className="mt-3 inline-flex items-center gap-2 rounded-sm bg-gold px-5 py-2.5 text-[14px] font-medium text-ink transition-colors hover:bg-[#c19f2c]" data-id="element-1129" data-mp="u72f0vij8w63">
            <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-1130" data-mp="3arxr55rafl47" />
            Send message
          </button>
        </form>
      </section>
    </div>;
}